import React from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  SUBSCRIPTION_ADD,
  SUBSCRIPTION_EDIT,
} from "../../../../services/ApiCalls";
import { useNavigate } from "react-router-dom";

function SubscriptionForm({ data }) {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: data?.name || "",
      price: data?.price || "",
      description: data?.description || "",
      start_date: data?.start_date || "",
      date_expires: data?.date_expires || "",
      status: data?.status || "inactive",
      _id: data?._id || "",
    },
  });

  const onSubmit = async (formData) => {
    try {
      let response;
      if (data?._id) {
        response = await SUBSCRIPTION_EDIT(formData);
      } else {
        delete formData._id;
        response = await SUBSCRIPTION_ADD(formData);
      }
      const { status, message } = response?.data || {};
      if (status == "failure")
        throw new Error(message || "Something went wrong!");
      toast.success(message || "Submitted successfully!");
      navigate("/subscription");
    } catch (error) {
      toast.error(error?.message || "Failed to submit");
    }
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto"
      style={{ maxWidth: 777 }}
    >
      <Row>
        <Col lg="6" className="my-2">
          <Form.Group controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              {...register("name", { required: "Name is required" })}
              isInvalid={!!errors.name}
            />
            {errors.name && (
              <div className="text-danger">{errors.name.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              {...register("description", {
                required: "Description is required",
              })}
              isInvalid={!!errors.description}
            />
            {errors.description && (
              <div className="text-danger">{errors.description.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="6" className="my-2">
          <Form.Group controlId="price">
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              {...register("price", {
                required: "price is required",
                valueAsNumber: true,
                min: {
                  value: 0,
                  message: "price cannot be negative",
                },
              })}
              isInvalid={!!errors.price}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === "-") {
                  e.preventDefault();
                }
              }}
            />
            {errors.price && (
              <div className="text-danger">{errors.price.message}</div>
            )}
          </Form.Group>
        </Col>

        {/* 🟡 Replaced Validity From with Start Date */}
        <Col lg="6" className="my-2">
          <Form.Group controlId="start_date">
            <Form.Label>Start Date</Form.Label>
            <Controller
              name="start_date"
              control={control}
              rules={{ required: "Start date is required" }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format="DD-MM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : null)
                  }
                  className="form-control h-100"
                  style={{ padding: "10.5px 10px" }}
                />
              )}
            />
            {errors.start_date && (
              <div className="text-danger">{errors.start_date.message}</div>
            )}
          </Form.Group>
        </Col>

        {/* 🟡 Replaced Validity To with Expiry Date */}
        <Col lg="6" className="my-2">
          <Form.Group controlId="date_expires">
            <Form.Label>Expiry Date</Form.Label>
            <Controller
              name="date_expires"
              control={control}
              rules={{
                required: "Expiry date is required",
                validate: (value) =>
                  !value ||
                  !watch("start_date") ||
                  dayjs(value).isAfter(dayjs(watch("start_date")))
                    ? true
                    : "Expiry date must be after start date",
              }}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  format="DD-MM-YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : null)
                  }
                  className="form-control h-100"
                  style={{ padding: "10.5px 10px" }}
                  disabledDate={(current) =>
                    watch("start_date")
                      ? current &&
                        current.isBefore(dayjs(watch("start_date")), "day")
                      : false
                  }
                />
              )}
            />
            {errors.date_expires && (
              <div className="text-danger">{errors.date_expires.message}</div>
            )}
          </Form.Group>
        </Col>
        
        <Col lg="6" className="my-2 subscription-input-wrapper">
          <Form.Group controlId="isActive">
            <Form.Label>Is Active</Form.Label>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Active/Inactive is required" }}
              render={({ field }) => (
                <Form.Check
                  type="switch"
                  id="active-toggle"
                  custom
                  checked={field.value === "active"}
                  onChange={(e) =>
                    field.onChange(e.target.checked ? "active" : "inactive")
                  }
                />
              )}
            />
            {errors.status && (
              <div className="text-danger">{errors.status.message}</div>
            )}
          </Form.Group>
        </Col>

        <Col lg="12" className="my-2">
          <Button variant="primary" type="submit">
            Submit
          </Button>
        </Col>
      </Row>
    </Form>
  );
}

export default SubscriptionForm;
